import { formatHourLabel } from '../utils/bookingUtils';
import DayCourtScopePicker from './DayCourtScopePicker';
import { BLOCK_BOOKING_MAX_END_HOUR } from '../constants/bookingConst';

// Start hours a block can begin on (0-23) and end hours it can finish on
// (1-24, where 24 is midnight at the END of the day). Built once — they
// never change.
const START_HOURS = Array.from({ length: 24 }, (_, h) => h);
const END_HOURS = Array.from({ length: BLOCK_BOOKING_MAX_END_HOUR }, (_, i) => i + 1);

/**
 * "12:00 MN" reads as the start of the day, so an END of 24 says which
 * midnight it means.
 * @param {number} hour - 1-24
 * @returns {string}
 */
const endHourLabel = (hour) => (hour === 24 ? '12:00 MN (end of day)' : formatHourLabel(hour));

/**
 * One block booking in Config -> Booking Settings: its name, the hour range
 * sold as a single booking, and the weekdays and courts it applies to.
 *
 * Days and courts go through DayCourtScopePicker: nothing ticked means
 * "all of them", the same convention rate bands and time-slot sets use.
 *
 * @param {object} props
 * @param {{id: string, label?: string, startHour: number, endHour: number, days?: number[], courts?: number[]}} props.block
 * @param {Array<{number: number, label: string}>} props.courtOptions - every configured court
 * @param {boolean} [props.overlaps] - another block claims some of the same hours
 * @param {(patch: object) => void} props.onChange - merges `patch` into the block
 * @param {() => void} props.onRemove
 * @returns {JSX.Element}
 */
function BlockBookingEditor({ block, courtOptions, overlaps = false, onChange, onRemove }) {
  const startHour = Number(block.startHour);
  const endHour = Number(block.endHour);
  const invalidRange = !(endHour > startHour);

  return (
    <div className={`bks-block${invalidRange || overlaps ? ' bks-block-warn' : ''}`}>
      <div className="bks-block-head">
        <input
          type="text"
          className="bks-block-name"
          value={block.label || ''}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="Name this block, e.g. Evening block"
        />
        <button type="button" className="bks-link-danger" onClick={onRemove}>
          Remove block
        </button>
      </div>

      <div className="bks-range">
        <label className="bks-field">
          <span>From</span>
          <select value={startHour} onChange={(e) => onChange({ startHour: Number(e.target.value) })}>
            {START_HOURS.map(h => <option key={h} value={h}>{formatHourLabel(h)}</option>)}
          </select>
        </label>
        <label className="bks-field">
          <span>To</span>
          <select value={endHour} onChange={(e) => onChange({ endHour: Number(e.target.value) })}>
            {END_HOURS.map(h => <option key={h} value={h}>{endHourLabel(h)}</option>)}
          </select>
        </label>
        {!invalidRange && (
          <span className="bks-range-note">
            {endHour - startHour} hour{endHour - startHour === 1 ? '' : 's'}, booked as one
          </span>
        )}
      </div>
      {invalidRange && <p className="bks-error">The end time must be after the start time.</p>}

      <DayCourtScopePicker
        days={block.days}
        courts={block.courts}
        courtOptions={courtOptions}
        onChange={onChange}
      />

      {overlaps && (
        <p className="bks-error">
          This block shares hours with another block on the same day and court. Only the first one listed
          will apply to those hours.
        </p>
      )}
    </div>
  );
}

export default BlockBookingEditor;
